import { LoadSpinner } from '@calizahq/react-components-v2'
import { Formik } from 'formik'
import React, { useState } from 'react'

import { HomeIssueMutations } from '@/graphql/mutations/homeIssue.mutations'
import { HomeInventoryItemQueries } from '@/graphql/queries/homeInventoryItems.queries'
import { useGraphql, useGraphqlMutation } from '@/hooks/useGraphql'
import useHomeLocations from '@/hooks/useHomeLocations/useHomeLocations'
import { HomeInventoryItem } from '@/types/HomeInventoryItem'
import { HomeIssue, HomeIssueType } from '@/types/HomeIssue'

import { HomeIssueForm } from './HomeIssueForm'
import { formSchema, FormValuesType } from './UpdateIssue.util'

type Props = {
  homeIssue: HomeIssue
  refetchOnSuccess?: () => void
  toggle: () => void
}

export const UpdateIssueForm = ({ toggle, homeIssue, refetchOnSuccess }: Props) => {
  const [error, setError] = useState<string>()

  const { data: homeInventoryItemData, isPending: isHomeInventoryItemLoading } = useGraphql<{
    homeInventoryItems: HomeInventoryItem[]
  }>(HomeInventoryItemQueries.QUERY_KEY, HomeInventoryItemQueries.getHomeInventoryItems())

  const { data: homeLocationData, isPending: isHomeLocationLoading } = useHomeLocations()

  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(HomeIssueMutations.updateHomeIssue(), {
    onSuccess: () => {
      refetchOnSuccess && refetchOnSuccess()
      toggle()
    },
    onError: () => {
      setError('Error updating Home Issue.')
    },
  })

  const homeInventoryItemOptions =
    homeInventoryItemData && homeInventoryItemData.homeInventoryItems
      ? homeInventoryItemData.homeInventoryItems.map((homeInventoryItem) => {
          return { label: homeInventoryItem.name, value: homeInventoryItem.id }
        })
      : []

  const homeLocationOptions =
    homeLocationData && homeLocationData.homeLocations
      ? homeLocationData.homeLocations.map((homeLocation) => {
          return { label: homeLocation.name, value: homeLocation.id }
        })
      : []

  const isLoading = isHomeInventoryItemLoading || isHomeLocationLoading

  const handleSubmit = (values: FormValuesType) => {
    const { attachments, homeInventoryItemId, type, ...restOfValues } = values
    const updatedHomeInventoryItemId =
      type === HomeIssueType.DAMAGE || type === HomeIssueType.REPLACEMENT_NEEDED ? homeInventoryItemId : undefined
    mutate({
      homeIssue: {
        ...restOfValues,
        homeIssueId: homeIssue.id,
        homeInventoryItemId: updatedHomeInventoryItemId,
        type: type,
      },
      attachments,
    })
  }

  return (
    <>
      {isLoading ? (
        <div className="flex h-48 w-full items-center justify-center ">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <Formik
          enableReinitialize={true}
          initialValues={{
            homeInventoryItemId: homeIssue.homeInventoryItemId || undefined,
            homeIssueId: homeIssue.id,
            homeLocationId: homeIssue.homeLocationId,
            notes: homeIssue.notes,
            type: homeIssue.type,
          }}
          onSubmit={handleSubmit}
          validateOnBlur={false}
          validationSchema={formSchema}
        >
          {({ values }) => {
            return (
              <HomeIssueForm
                error={error}
                homeInventoryItemOptions={homeInventoryItemOptions}
                homeIssueType={values.type}
                homeLocationOptions={homeLocationOptions}
                isMutationLoading={isMutationLoading}
                toggle={toggle}
              />
            )
          }}
        </Formik>
      )}
    </>
  )
}
